/**
 * AutoSplitDialog — one-click detection + preview + batch extraction of
 * a jewellery render's components.
 *
 * FLOW:
 *   1. Dialog opens with a "Detect components (1 credit)" CTA.
 *   2. Click → POST /api/studio/auto-split/detect/{gen_id}.
 *   3. Backend returns 3–8 components with names + bounding boxes.
 *   4. UI renders a preview grid — each tile shows a client-side crop
 *      of the source image using the bbox, plus name (editable) + a
 *      checkbox. Total cost updates live.
 *   5. Click "Extract N components (N credits)" → POST
 *      /api/studio/auto-split/extract/{gen_id} with only checked rows.
 *   6. New gallery entries land in the studio via `onExtracted`.
 *
 * NON-INVASIVE: this dialog is a self-contained new component. It does
 * NOT modify the existing SplitComponentDialog, RefineDialog, or any
 * other studio flow.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, ScanSearch, RefreshCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { renderImageUrl } from "@/lib/imageUrl";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AutoSplitDialog({
  target,
  open,
  onOpenChange,
  onExtracted,
  onQuotaChange,
}) {
  const [phase, setPhase] = useState("idle"); // idle | detecting | preview | extracting
  const [components, setComponents] = useState([]); // [{name, bbox, category, selected, editing}]
  const [detectionId, setDetectionId] = useState(null);
  const [error, setError] = useState(null);
  const [jobProgress, setJobProgress] = useState({ done: 0, total: 0 });
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  // Track a polling interval so we can cancel it if the dialog closes
  // mid-job (the backend job keeps running — user can just re-open the
  // gallery to see the results).
  const pollTimerRef = useRef(null);
  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  // Reset state each time the dialog closes so a fresh open starts clean.
  useEffect(() => {
    if (!open) {
      stopPolling();
      setPhase("idle");
      setComponents([]);
      setDetectionId(null);
      setError(null);
      setImgLoaded(false);
      setJobProgress({ done: 0, total: 0 });
    }
  }, [open]);

  // Safety net — always clear the interval on unmount.
  useEffect(() => () => stopPolling(), []);

  const selectedCount = useMemo(
    () => components.filter((c) => c.selected).length,
    [components],
  );

  if (!target) return null;

  const sourceUrl = renderImageUrl(BACKEND, target);

  const runDetect = async () => {
    setPhase("detecting");
    setError(null);
    try {
      const res = await axios.post(
        `${API}/studio/auto-split/detect/${target.id}`,
        { max_components: 8 },
      );
      const data = res.data || {};
      const rows = (data.components || []).map((c) => ({
        ...c,
        selected: true,
        editing: false,
      }));
      setComponents(rows);
      setDetectionId(data.detection_id);
      onQuotaChange?.(data.quota);
      setPhase("preview");
      toast.success(`Detected ${rows.length} components · 1 credit spent`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Detection failed.";
      setError(msg);
      setPhase("idle");
      toast.error(msg);
    }
  };

  const runExtract = async () => {
    const selected = components.filter((c) => c.selected);
    if (selected.length === 0) {
      toast.error("Select at least one component to extract.");
      return;
    }
    setPhase("extracting");
    setError(null);
    setJobProgress({ done: 0, total: selected.length });
    let jobId;
    try {
      // Client-generated idempotency key — the backend uses this to
      // de-duplicate retries after a Cloudflare 504 / network flake so
      // the user is never double-charged for the same batch.
      const idemKey =
        (window.crypto?.randomUUID?.() ||
          `autosplit-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const res = await axios.post(
        `${API}/studio/auto-split/extract/${target.id}`,
        {
          components: selected.map((c) => ({ name: c.name, bbox: c.bbox })),
          detection_id: detectionId,
        },
        { headers: { "Idempotency-Key": idemKey } },
      );
      const data = res.data || {};
      jobId = data.job_id;
      if (!jobId) throw new Error("Backend did not return a job id.");
      setJobProgress({ done: 0, total: data.total || selected.length });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Extraction failed.";
      setError(msg);
      setPhase("preview");
      toast.error(msg);
      return;
    }

    // Poll for job completion every 2 s. The backend job survives
    // Cloudflare timeouts because each poll is a fresh sub-second
    // request. On terminal status (`done` | `failed`) we surface the
    // final result and close the dialog.
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/studio/auto-split/job/${jobId}`);
        const s = res.data || {};
        setJobProgress({ done: s.done || 0, total: s.total || 0 });
        if (s.status === "done" || s.status === "failed") {
          stopPolling();
          if (s.status === "failed") {
            const err = s.error || "Extraction failed.";
            setError(err);
            setPhase("preview");
            toast.error(err);
            return;
          }
          onExtracted?.(s.entries || []);
          onQuotaChange?.(s.quota);
          const nOk = (s.entries || []).length;
          const nFail = (s.failures || []).length;
          if (nFail > 0) {
            toast.warning(`Extracted ${nOk} · ${nFail} failed · ${s.credits_charged} credits`);
          } else {
            toast.success(`Extracted ${nOk} components · ${s.credits_charged} credits`);
          }
          onOpenChange?.(false);
        }
      } catch (e) {
        // Transient poll failure — keep polling. We only bail out on
        // 4xx which indicates a real problem (401/404/403).
        const status = e?.response?.status;
        if (status && status >= 400 && status < 500) {
          stopPolling();
          const msg = e?.response?.data?.detail || "Job vanished — please retry.";
          setError(msg);
          setPhase("preview");
          toast.error(msg);
        }
      }
    }, 2000);
  };

  const toggle = (i) =>
    setComponents((prev) =>
      prev.map((c, idx) => (idx === i ? { ...c, selected: !c.selected } : c)),
    );
  const startEdit = (i) =>
    setComponents((prev) =>
      prev.map((c, idx) => (idx === i ? { ...c, editing: true } : c)),
    );
  const renameAt = (i, name) =>
    setComponents((prev) =>
      prev.map((c, idx) => (idx === i ? { ...c, name } : c)),
    );
  const endEdit = (i) =>
    setComponents((prev) =>
      prev.map((c, idx) => (idx === i ? { ...c, editing: false } : c)),
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="auto-split-dialog"
        className="max-w-4xl bg-[#0B0B0B] border border-white/10 text-white p-0 overflow-hidden"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/5">
          <DialogTitle className="text-white text-base flex items-center gap-2">
            <ScanSearch className="w-4 h-4 text-[#D4AF37]" />
            Auto-split components
          </DialogTitle>
          <DialogDescription className="text-white/50 text-xs">
            Gemini detects each distinct component of this piece so you can
            extract them all in one batch instead of lassoing manually.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-12 gap-4 p-5">
          {/* Left — source render + optional bbox overlay */}
          <div className="col-span-12 md:col-span-5">
            <div className="relative aspect-square rounded-lg overflow-hidden bg-[#0A0A0A] border border-white/8">
              <img
                ref={imgRef}
                src={sourceUrl}
                alt="Source render"
                onLoad={() => setImgLoaded(true)}
                className="absolute inset-0 w-full h-full object-contain"
              />
              {phase === "preview" && imgLoaded &&
                components.map((c, i) =>
                  c.selected ? (
                    <div
                      key={i}
                      className="absolute border border-[#D4AF37]/70 pointer-events-none"
                      style={{
                        left: `${c.bbox[0] * 100}%`,
                        top: `${c.bbox[1] * 100}%`,
                        width: `${(c.bbox[2] - c.bbox[0]) * 100}%`,
                        height: `${(c.bbox[3] - c.bbox[1]) * 100}%`,
                        boxShadow: "0 0 0 1px rgba(0,0,0,0.5)",
                      }}
                    >
                      <span className="absolute -top-4 left-0 text-[9px] uppercase tracking-[0.16em] text-[#D4AF37] bg-black/70 px-1.5 py-0.5 rounded">
                        {i + 1}
                      </span>
                    </div>
                  ) : null,
                )
              }
            </div>
            <div className="mt-2 text-[10px] text-white/40">
              {target.style ? `Style: ${target.style}` : ""}
            </div>
          </div>

          {/* Right — action panel */}
          <div className="col-span-12 md:col-span-7 flex flex-col">
            {phase === "idle" && (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-10">
                <ScanSearch className="w-8 h-8 text-[#D4AF37]/70 mb-3" />
                <div className="text-sm text-white/85 mb-1">Ready to detect components</div>
                <div className="text-[11px] text-white/50 leading-relaxed max-w-xs mb-5">
                  Gemini will scan this piece and list 3–8 distinct
                  components with their bounding boxes. Preview them, deselect
                  any false positives, then extract the rest at once.
                </div>
                <button
                  onClick={runDetect}
                  data-testid="auto-split-detect-btn"
                  className="inline-flex items-center gap-2 text-xs px-4 py-2 rounded-full bg-[#D4AF37] text-black hover:brightness-110 transition"
                >
                  <ScanSearch className="w-4 h-4" />
                  Detect components (1 credit)
                </button>
                {error && (
                  <div className="mt-4 text-[11px] text-red-400 max-w-xs">{error}</div>
                )}
              </div>
            )}

            {phase === "detecting" && (
              <div className="flex-1 flex flex-col items-center justify-center py-10">
                <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mb-3" />
                <div className="text-sm text-white/85">Detecting components…</div>
                <div className="text-[11px] text-white/45 mt-1">This takes ~20 seconds.</div>
              </div>
            )}

            {phase === "preview" && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">
                    Detected · {components.length}
                  </div>
                  <button
                    onClick={runDetect}
                    className="text-[10px] uppercase tracking-[0.18em] text-white/50 hover:text-white/80 inline-flex items-center gap-1"
                    title="Re-run detection (1 credit)"
                  >
                    <RefreshCcw className="w-3 h-3" /> Re-detect
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto pr-1 space-y-2 max-h-[380px]">
                  {components.map((c, i) => (
                    <label
                      key={i}
                      className={`flex items-center gap-3 p-2.5 rounded-lg border transition cursor-pointer ${
                        c.selected
                          ? "bg-[#141414] border-[#D4AF37]/30"
                          : "bg-[#0F0F0F] border-white/8 opacity-55"
                      }`}
                      data-testid={`auto-split-row-${i}`}
                    >
                      <input
                        type="checkbox"
                        checked={c.selected}
                        onChange={() => toggle(i)}
                        className="w-4 h-4 accent-[#D4AF37]"
                        data-testid={`auto-split-row-${i}-checkbox`}
                      />
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] font-medium">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        {c.editing ? (
                          <input
                            autoFocus
                            value={c.name}
                            onChange={(e) => renameAt(i, e.target.value)}
                            onBlur={() => endEdit(i)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === "Escape") endEdit(i);
                            }}
                            maxLength={120}
                            className="w-full bg-[#0A0A0A] border border-[#D4AF37]/30 rounded px-2 py-1 text-xs text-white outline-none"
                            data-testid={`auto-split-row-${i}-input`}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              startEdit(i);
                            }}
                            className="text-xs text-white/90 truncate block text-left w-full hover:text-[#D4AF37]"
                            title="Click to rename"
                          >
                            {c.name}
                          </button>
                        )}
                        <div className="text-[9px] uppercase tracking-[0.14em] text-white/40 mt-0.5">
                          {c.category || "component"}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="pt-3 mt-3 border-t border-white/5 flex items-center justify-between gap-3">
                  <div className="text-[11px] text-white/60">
                    <span className="text-white/85 font-medium">{selectedCount}</span>
                    <span className="text-white/45"> of {components.length} selected · </span>
                    <span className="text-[#D4AF37]">{selectedCount} credits</span>
                  </div>
                  <button
                    onClick={runExtract}
                    disabled={selectedCount === 0}
                    data-testid="auto-split-extract-btn"
                    className="inline-flex items-center gap-2 text-xs px-4 py-2 rounded-full bg-[#D4AF37] text-black hover:brightness-110 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Extract {selectedCount} component{selectedCount === 1 ? "" : "s"}
                  </button>
                </div>
                {error && (
                  <div className="mt-2 text-[11px] text-red-400">{error}</div>
                )}
              </>
            )}

            {phase === "extracting" && (
              <div className="flex-1 flex flex-col items-center justify-center py-10">
                <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mb-3" />
                <div className="text-sm text-white/85">
                  Extracting {jobProgress.total} component{jobProgress.total === 1 ? "" : "s"}…
                </div>
                <div className="text-[11px] text-white/55 mt-2 tabular-nums">
                  {jobProgress.done} / {jobProgress.total} done
                </div>
                <div className="mt-3 w-56 h-1.5 rounded-full bg-white/8 overflow-hidden">
                  <div
                    className="h-full bg-[#D4AF37] transition-all duration-500"
                    style={{
                      width: `${jobProgress.total > 0
                        ? Math.min(100, (jobProgress.done / jobProgress.total) * 100)
                        : 0}%`,
                    }}
                  />
                </div>
                <div className="text-[11px] text-white/45 mt-3 max-w-xs text-center">
                  Runs safely in the background — you can close this dialog
                  and the components will appear in your gallery when done.
                </div>
              </div>
            )}
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}
